import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  FileText,
  TrendingUp,
  Users,
  Calculator,
  Plus,
  ArrowRight,
  IndianRupee,
  Clock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SalesDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await fetch(`${API}/api/sales/dashboard/stats`, {
        credentials: "include",
      });
      const data = await res.json();
      
      if (data.success) {
        setStats(data.stats);
        setRecentQuotes(data.recent?.quotes || []);
        setRecentLeads(data.recent?.leads || []);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-100 text-blue-700",
      contacted: "bg-yellow-100 text-yellow-700",
      demo_scheduled: "bg-purple-100 text-purple-700",
      proposal_sent: "bg-orange-100 text-orange-700",
      negotiating: "bg-pink-100 text-pink-700",
      won: "bg-green-100 text-green-700",
      lost: "bg-red-100 text-red-700",
      draft: "bg-gray-100 text-gray-700",
      sent: "bg-blue-100 text-blue-700",
      accepted: "bg-green-100 text-green-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const pipelineStages = ["new", "contacted", "demo_scheduled", "proposal_sent", "negotiating"];
  const pipelineValue = stats?.pipeline_value || 0;

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => navigate("/sales/calculator")}
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          data-testid="quick-calculator-btn"
        >
          <Calculator className="w-4 h-4 mr-2" />
          Pricing Calculator
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/sales/quotes")}
          data-testid="quick-quotes-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          View Quotes
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/sales/leads")}
          data-testid="quick-leads-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Lead
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Schools</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.schools?.total || 0}</p>
                <p className="text-xs text-green-600 mt-1">
                  {stats?.schools?.active || 0} active
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pipeline Value</p>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(pipelineValue)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Active deals
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Quotes</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.quotes?.total || 0}</p>
                <p className="text-xs text-blue-600 mt-1">
                  {stats?.quotes?.this_month || 0} this month
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Schools</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.schools?.pending || 0}</p>
                <p className="text-xs text-orange-600 mt-1">
                  Awaiting approval
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Overview */}
      <Card className="bg-white shadow-sm border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg text-gray-900">Pipeline Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {pipelineStages.map((stage) => {
              const stageData = stats?.leads?.[stage] || { count: 0, value: 0 };
              return (
                <div 
                  key={stage}
                  className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100"
                >
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    {stage.replace("_", " ")}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">{stageData.count}</p>
                  <p className="text-sm text-gray-600">{formatCurrency(stageData.value)}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Quotes */}
        <Card className="bg-white shadow-sm border-gray-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-gray-900">Recent Quotes</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/sales/quotes")}
              className="text-blue-600"
            >
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentQuotes.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No quotes yet</p>
            ) : (
              <div className="space-y-3">
                {recentQuotes.map((quote, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{quote.school_name}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(quote.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(quote.total_amount)}
                      </p>
                      <Badge className={getStatusColor(quote.status)}>
                        {quote.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Leads */}
        <Card className="bg-white shadow-sm border-gray-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-gray-900">Recent Leads</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/sales/leads")}
              className="text-blue-600"
            >
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentLeads.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No leads yet</p>
            ) : (
              <div className="space-y-3">
                {recentLeads.map((lead, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{lead.school_name}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(lead.expected_value)}
                      </p>
                      <Badge className={getStatusColor(lead.status)}>
                        {lead.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
